import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { OrganizationLayout } from '@/layouts/organization-layout'
import { getDateLocale } from '@/lib/i18n'
import { isAdmin as checkIsAdmin } from '@/lib/auth'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog'
import { ArchiveProjectDialog } from '@/components/projects/ArchiveProjectDialog'
import {
  ArchiveIcon,
  ExternalLinkIcon,
  AlertCircleIcon,
  ArrowLeftIcon,
  UserPlusIcon,
  XIcon,
  TriangleAlertIcon,
  UsersIcon
} from 'lucide-react'

interface Project {
  id: number
  name: string
  slug: string
  description: string | null
  is_archived: boolean
  created_at: string
  updated_at: string
}

interface Organization {
  id: number
  name: string
}

interface ProjectMember {
  id: number
  user_id: number
  name: string
  email: string
  avatar_url: string | null
  assigned_by: string
  created_at: string
}

interface ExternalUser {
  id: number
  name: string
  email: string
  avatar_url: string | null
}

interface OrganizationProjectsShowProps {
  organization: Organization
  project: Project
  project_members: ProjectMember[]
  external_users: ExternalUser[]
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
      organization_role: string
    }
  }
}

function Show({ project, project_members = [], external_users = [], auth }: OrganizationProjectsShowProps) {
  const { t } = useTranslation('organizations')
  const isAdmin = checkIsAdmin(auth.user)
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [isAdding, setIsAdding] = useState(false)
  const [removingMemberId, setRemovingMemberId] = useState<number | null>(null)

  const handleAddMember = () => {
    if (!selectedUserId) return
    setIsAdding(true)

    router.post(`/organization/projects/${project.id}/project_memberships`, {
      user_id: selectedUserId
    }, {
      onSuccess: () => {
        setSelectedUserId('')
      },
      onFinish: () => {
        setIsAdding(false)
      }
    })
  }

  const handleRemoveMember = (membershipId: number) => {
    setRemovingMemberId(membershipId)
    router.delete(`/organization/projects/${project.id}/project_memberships/${membershipId}`, {
      onFinish: () => {
        setRemovingMemberId(null)
      }
    })
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col">
        <div className="px-4 py-6 lg:px-6">
          <div className="mx-auto max-w-4xl space-y-8">

            {/* Back link */}
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground"
              asChild
            >
              <Link href="/organization/projects">
                <ArrowLeftIcon className="size-4" />
                {t('projects.show.backLink')}
              </Link>
            </Button>

            {/* Project Header */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold">{project.name}</h2>
                  {project.is_archived ? (
                    <Badge variant="warning">{t('projects.status.archived')}</Badge>
                  ) : (
                    <Badge variant="success">{t('projects.status.active')}</Badge>
                  )}
                </div>
                {project.slug && (
                  <p className="mt-1 text-sm text-muted-foreground">{project.slug}</p>
                )}
              </div>
              {!project.is_archived && (
                <Button asChild className="max-sm:w-full">
                  <Link href={`/projects/${project.id}`}>
                    <ExternalLinkIcon className="mr-2 h-4 w-4" />
                    {t('projects.show.openButton')}
                  </Link>
                </Button>
              )}
            </div>

            {/* Archived Notice */}
            {project.is_archived && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
                <div className="flex items-start gap-3">
                  <AlertCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">{t('projects.show.archivedNotice.title')}</p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      {t('projects.show.archivedNotice.message')}{' '}
                      <a
                        href="mailto:support@cayu.app"
                        className="font-medium underline hover:no-underline"
                      >
                        support@cayu.app
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Separator />

            {/* Project Details */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t('projects.show.details.title')}
              </h2>
            </div>

            <div className="rounded-lg border p-4">
              <p className="text-xs font-medium text-muted-foreground">{t('projects.show.details.description')}</p>
              <p className="mt-1 text-sm">
                {project.description || (
                  <span className="text-muted-foreground italic">{t('projects.show.details.noDescription')}</span>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-lg border p-4">
                <p className="text-xs font-medium text-muted-foreground">{t('projects.show.details.created')}</p>
                <p className="mt-1 text-sm font-semibold">
                  {new Date(project.created_at).toLocaleDateString(getDateLocale(), {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs font-medium text-muted-foreground">{t('projects.show.details.updated')}</p>
                <p className="mt-1 text-sm font-semibold">
                  {new Date(project.updated_at).toLocaleDateString(getDateLocale(), {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>

            {/* External Members Section */}
            <div className="mt-6 space-y-6">
              {/* Add External User */}
              {isAdmin && external_users.length > 0 && (
                <Card className="shadow-none">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <UserPlusIcon className="h-5 w-5" />
                      {t('projects.show.members.addTitle')}
                    </CardTitle>
                    <CardDescription>{t('projects.show.members.addDescription')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder={t('projects.show.members.selectUserPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {external_users.map((user) => (
                            <SelectItem key={user.id} value={String(user.id)}>
                              {user.name} ({user.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={handleAddMember} disabled={!selectedUserId || isAdding} className="flex-shrink-0">
                        <UserPlusIcon className="mr-2 h-4 w-4" />
                        {isAdding ? t('projects.show.members.adding') : t('projects.show.members.addButton')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Current Members */}
              <Card className="shadow-none">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UsersIcon className="h-5 w-5" />
                    {t('projects.show.members.title', { count: project_members.length })}
                  </CardTitle>
                  <CardDescription>{t('projects.show.members.description')}</CardDescription>
                </CardHeader>
                <CardContent>
                  {project_members.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      {t('projects.show.members.noMembers')}
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {project_members.map((member) => {
                        const initials = member.name.split(' ').map(n => n[0]).join('').toUpperCase()

                        return (
                          <div key={member.id} className="flex flex-wrap items-center justify-between gap-3 p-4 bg-muted rounded-lg border border-border">
                            <div className="flex items-center gap-4 min-w-0 flex-1">
                              <Avatar className="h-10 w-10 flex-shrink-0">
                                <AvatarImage src={member.avatar_url || undefined} alt={member.name} />
                                <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg border border-primary/20">
                                  {initials}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0 flex-1">
                                <p className="text-base font-medium text-foreground truncate">{member.name}</p>
                                <p className="text-sm text-muted-foreground truncate">{member.email}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {t('projects.show.members.assignedBy', { name: member.assigned_by })}
                                </p>
                              </div>
                            </div>
                            {isAdmin && (
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    >
                                      <XIcon className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader className="items-center">
                                      <div className="bg-orange-100 mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
                                        <TriangleAlertIcon className="text-orange-600 size-6" />
                                      </div>
                                      <AlertDialogTitle>{t('projects.show.members.removeTitle')}</AlertDialogTitle>
                                      <AlertDialogDescription className="text-center">
                                        {t('projects.show.members.removeDescription', { name: member.name })}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>{t('actions.cancel')}</AlertDialogCancel>
                                      <AlertDialogAction
                                        className="bg-orange-600 hover:bg-orange-700 focus-visible:ring-orange-600 text-white"
                                        onClick={() => handleRemoveMember(member.id)}
                                        disabled={removingMemberId === member.id}
                                      >
                                        {removingMemberId === member.id ? t('projects.show.members.removing') : t('projects.show.members.removeButton')}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Danger Zone */}
            {!project.is_archived && (
              <>
                <Separator />

                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-destructive">
                    {t('projects.show.dangerZone.title')}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('projects.show.dangerZone.description')}
                  </p>
                </div>

                <Button variant="destructive" onClick={() => setIsArchiveDialogOpen(true)} className="max-sm:w-full">
                  <ArchiveIcon className="mr-2 h-4 w-4" />
                  {t('projects.show.dangerZone.archiveButton')}
                </Button>
                <ArchiveProjectDialog
                  project={project}
                  open={isArchiveDialogOpen}
                  onOpenChange={setIsArchiveDialogOpen}
                />
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  )
}

Show.layout = (page: React.ReactNode) => <OrganizationLayout>{page}</OrganizationLayout>

export default Show
